import React, { useEffect, useState, useCallback } from "react";
import { Background, Page, IconBack, IconDownload } from "./style";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import api from "../../Services/api";
import { Alert, CloseAlert } from "../../utils/Alert";
import CarteirinhaVertiPdf from "../../Components/Pdf/CarteirinhaVertiPdf";
import { useLocation, useNavigate } from "react-router-dom";

export default function PdfCracha() {
  const [pages, setPages] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingFotos, setLoadingFotos] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const ROWS_PER_PAGE = 4; // 4 carteirinhas por página

  // Handle navigation fallback if no state is provided
  useEffect(() => {
    if (!location.state) {
      try {
        navigate(-1);
      } catch {
        navigate("/secretaria");
      }
      return;
    }

    // Only show loading indicator when actually loading
    if (isLoading || loadingFotos) {
      Alert({ tipo: "Loading", mensagem: "O pdf está sendo gerado!" });
    } else {
      CloseAlert();
    }
  }, [isLoading, loadingFotos, navigate, location]);

  // Data initialization effect
  useEffect(() => {
    if (!location.state) return;

    if (location.state.alunos) {
      setAlunos(location.state.alunos);
      setIsLoading(false);
    } else {
      const { nome_curso, convenio, dataInicio, validade } = location.state;
      fetchAlunos(nome_curso, convenio, dataInicio, validade);
    }
  }, [location.state]);

  // Calculate pages based on alunos data
  useEffect(() => {
    if (alunos.length > 0) {
      const totalPaginas = Math.ceil(alunos.length / ROWS_PER_PAGE);
      setPages(Array.from({ length: totalPaginas }, (_, i) => i));
    }
  }, [alunos]);

  // Fetch students data from API
  const fetchAlunos = useCallback(async (curso, convenio, dt_inicio, validade) => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/aluno/listar?nome_curso=${curso}&convenio=${convenio}&dt_inicio=${dt_inicio}`, {
        headers: {
          "x-limite": 10000,
        },
      });

      if (!data?.alunos?.length) {
        Alert({ tipo: "Erro", mensagem: "Nenhum aluno encontrado." });
        setIsLoading(false);
        return;
      }

      const formattedData = data.alunos.map((aluno) => ({
        ...aluno,
        dt_termino: validade, // Substitui o valor vindo do backend
      }));

      setAlunos(formattedData);
    } catch (err) {
      console.error("Erro ao buscar alunos:", err);
      Alert({ tipo: "Erro", mensagem: "Nenhum aluno encontrado." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle PDF download with optimized rendering
  const handleDownloadPDF = useCallback(async () => {
    setIsDownloading(true);
    Alert({ tipo: "Loading", mensagem: "O pdf está sendo baixado!" });

    // Log the generation attempt
    try {
      await api.post("/usuario/log-gerarcracha", {
        data: { curso: location?.state?.nome_curso },
      });
    } catch (error) {
      console.error("Erro ao registrar log de geração:", error);
    }

    const pages = document.querySelectorAll(".page");
    if (!pages.length) {
      console.error("Nenhuma página encontrada!");
      CloseAlert();
      setIsDownloading(false);
      return;
    }

    try {
      const pdf = new jsPDF("p", "mm", "a4");

      for (let i = 0; i < pages.length; i++) {
        // Improved canvas rendering with better quality settings
        const canvas = await html2canvas(pages[i], {
          scale: 2.5, // Higher scale for better quality
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png", 1.0); // Max quality
        const imgWidth = 210; // A4 width in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      }

      pdf.save(`carteirinhas_${location?.state?.nome_curso || "alunos"}.pdf`);
      Alert({ tipo: "Sucesso-popup", mensagem: "PDF gerado com sucesso!" });
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      Alert({ tipo: "Erro", mensagem: "Houve um problema ao gerar o PDF. Tente novamente mais tarde!" });
    } finally {
      setIsDownloading(false);
      CloseAlert();
    }
  }, [location?.state?.nome_curso]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    try {
      navigate(-1);
    } catch {
      navigate("/secretaria");
    }
  }, [navigate]);

  if (isLoading && alunos.length === 0) {
    return (
      <Background>
        <div className="loading-container">
          <p>Carregando dados...</p>
        </div>
      </Background>
    );
  }

  return (
    <Background>
      {!isDownloading && (
        <>
          <IconBack title="Voltar" onClick={handleBack} />
          <IconDownload title="Baixar" onClick={handleDownloadPDF} />
        </>
      )}

      {pages.map((pageIndex) => (
        <Page key={`page-${pageIndex}`} className="page">
          <div className="grid-layout">
            {alunos.slice(pageIndex * ROWS_PER_PAGE, (pageIndex + 1) * ROWS_PER_PAGE).map((aluno, index) => (
              <CarteirinhaVertiPdf Dados={aluno} key={`card-${pageIndex}-${index}`} setLoadingFotos={setLoadingFotos} />
            ))}
          </div>
        </Page>
      ))}

      {alunos.length === 0 && !isLoading && (
        <div className="no-data">
          <p>Nenhum aluno encontrado.</p>
        </div>
      )}
    </Background>
  );
}
